import {
  forwardRef,
  ReactNode,
  MutableRefObject,
  ReactElement,
  Children,
  isValidElement,
  useEffect,
  useMemo,
} from 'react';
import { FloatingList } from '@floating-ui/react';
import { css, cx } from '@linaria/atomic';
import { onlyText } from 'react-children-utilities';
import { theme } from '@frontend/theme';
import { StylesProp, splitStylesProp } from '../../../../helpers/styles-prop';
import { TextLink } from '../../../text-link/text-link.component';
import { useFormField } from '../../hooks/use-form-field/use-form-field';
import { SearchField } from '../search-field/search-field.component';

const componentTextLinkStyle1 = css`
  width: fit-content;
`;

const menuContainerStyle = css`
  outline: none;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
`;

const menuSelectionBarStyle = css`
  display: flex;
  justify-content: space-between;
  padding: ${theme.spacing(0, 2)};
`;

const menuScrollAreaStyle = css`
  overflow: auto;
`;

const searchContainerStyle = css`
  padding: ${theme.spacing(2, 2, 0)};
`;

const singleSelectionBarStyle = css`
  display: grid;
  justify-content: end;
`;

const singleSelectionBarWithSearchStyle = css`
  padding: ${theme.spacing(0, 2)};
`;

const singleSelectionBarWithoutSearchStyle = css`
  padding: ${theme.spacing(2, 2, 0)};
`;

export const Menu = forwardRef<
  HTMLDivElement,
  {
    floatingProps: Record<string, unknown>;
    children: ReactNode;
    refs: {
      listItemRefs: MutableRefObject<(HTMLLIElement | null)[]>;
      labelsRef: MutableRefObject<(string | null)[]>;
      valuesRef: MutableRefObject<(string | null)[]>;
    };
    clearSelection: () => void;
    selectAll: (filter: (child: ReactElement) => boolean) => void;
    clearSelectionLabel?: string;
    selectAllLabel?: string;
    trackingIdBase?: string;
    showSearchBar?: boolean;
    showSelectionBar?: boolean;
    showSelectAll?: boolean;
    topContent?: ReactNode;
    searchContainerStyles?: StylesProp;
    customSearchComparator?: (value: string, searchTerm: string) => boolean;
  }
>(
  (
    {
      floatingProps,
      children,
      refs,
      clearSelection,
      selectAll,
      clearSelectionLabel = 'Clear',
      selectAllLabel = 'Select All',
      trackingIdBase,
      showSearchBar = true,
      searchContainerStyles,
      showSelectionBar = true,
      showSelectAll = true,
      topContent,
      customSearchComparator,
    },
    ref
  ) => {
    const field = useFormField({ type: 'text' });

    const { css: searchContainerCss, className: searchContainerClassName } = splitStylesProp(searchContainerStyles);

    // Create values array synchronized with children structure, handling nested Groups
    const flattenedChildren = useMemo(() => {
      return Children.toArray(children).flatMap((child) => {
        if (
          isValidElement(child) &&
          'label' in child.props &&
          'children' in child.props &&
          typeof child.props.label === 'string'
        ) {
          // This is likely a Group component
          return Children.toArray(child.props.children);
        }
        return child;
      });
    }, [children]);

    const filtered = flattenedChildren.filter((child) =>
      customSearchComparator
        ? customSearchComparator(onlyText(child), field.value)
        : onlyText(child).toLowerCase().includes(field.value.toLowerCase())
    );

    useEffect(() => {
      const valuesRef = refs.valuesRef;
      valuesRef.current = filtered.map((child) => {
        if (isValidElement(child) && child.props) {
          return (child.props as any).value || null;
        }
        return null;
      });
    }, [filtered, refs.valuesRef]);

    return (
      <div className={menuContainerStyle} ref={ref} {...floatingProps}>
        {!!topContent && topContent}
        {showSearchBar && (
          <div className={cx(searchContainerStyle, searchContainerClassName)} css={searchContainerCss}>
            <SearchField
              {...field}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Tab') {
                  return;
                }
                e.stopPropagation();
              }}
              name='search'
            />
          </div>
        )}
        {showSelectionBar && (
          <div className={menuSelectionBarStyle}>
            {showSelectAll ? (
              <TextLink
                weight='bold'
                onKeyDown={(e) => {
                  if (e.key === ' ') {
                    e.preventDefault();
                  }
                }}
                onClick={() => {
                  selectAll(
                    (child) =>
                      field.value === '' ||
                      (customSearchComparator
                        ? customSearchComparator(onlyText(child), field.value)
                        : onlyText(child).toLowerCase().includes(field.value.toLowerCase()))
                  );
                }}
                trackingId={`${trackingIdBase}-select-all`}
              >
                {selectAllLabel}
              </TextLink>
            ) : (
              <div></div>
            )}
            <TextLink
              weight='bold'
              onKeyDown={(e) => {
                if (e.key === ' ') {
                  e.preventDefault();
                }
              }}
              onClick={() => {
                clearSelection();
              }}
              trackingId={`${trackingIdBase}-clear-all`}
            >
              {clearSelectionLabel}
            </TextLink>
          </div>
        )}
        <div className={menuScrollAreaStyle}>
          <FloatingList elementsRef={refs.listItemRefs} labelsRef={refs.labelsRef}>
            {/* TODO: keep grouping when filtering as well */}
            {field.value ? filtered : children}
          </FloatingList>
        </div>
      </div>
    );
  }
);

Menu.displayName = 'Menu';

export const SingleSelectionMenu = forwardRef<
  HTMLDivElement,
  {
    floatingProps: Record<string, unknown>;
    children: ReactNode;
    refs: {
      listItemRefs: MutableRefObject<(HTMLLIElement | null)[]>;
      labelsRef: MutableRefObject<(string | null)[]>;
      valuesRef: MutableRefObject<(string | null)[]>;
    };
    clearSelection: () => void;
    clearSelectionLabel?: string;
    trackingIdBase?: string;
    showSearchBar?: boolean;
    showSelectionBar?: boolean;
  }
>(
  (
    {
      floatingProps,
      children,
      refs,
      clearSelection,
      clearSelectionLabel = 'Clear',
      trackingIdBase,
      showSearchBar = true,
      showSelectionBar = true,
    },
    ref
  ) => {
    const field = useFormField({ type: 'text' });

    // Create values array synchronized with children structure, handling nested Groups
    const flattenedChildren = useMemo(() => {
      return Children.toArray(children).flatMap((child) => {
        if (
          isValidElement(child) &&
          'label' in child.props &&
          'children' in child.props &&
          typeof child.props.label === 'string'
        ) {
          // This is likely a Group component
          return Children.toArray(child.props.children);
        }
        return child;
      });
    }, [children]);

    const filtered = flattenedChildren.filter((child) =>
      onlyText(child).toLowerCase().includes(field.value.toLowerCase())
    );

    useEffect(() => {
      const valuesRef = refs.valuesRef;
      valuesRef.current = flattenedChildren.map((child) => {
        if (isValidElement(child) && child.props) {
          return (child.props as any).value || null;
        }
        return null;
      });
    }, [filtered, flattenedChildren, refs.valuesRef]);

    return (
      <div className={menuContainerStyle} ref={ref} {...floatingProps}>
        {showSearchBar && (
          <div className={searchContainerStyle}>
            <SearchField
              {...field}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Tab') {
                  return;
                }
                e.stopPropagation();
              }}
              name='search'
            />
          </div>
        )}
        {showSelectionBar && (
          <div
            className={cx(
              singleSelectionBarStyle,
              showSearchBar ? singleSelectionBarWithSearchStyle : singleSelectionBarWithoutSearchStyle
            )}
          >
            <TextLink
              weight='bold'
              onKeyDown={(e) => {
                if (e.key === ' ') {
                  e.preventDefault();
                }
              }}
              onClick={() => {
                clearSelection();
              }}
              className={componentTextLinkStyle1}
              trackingId={`${trackingIdBase}-clear-all`}
            >
              {clearSelectionLabel}
            </TextLink>
          </div>
        )}
        <div className={menuScrollAreaStyle}>
          <FloatingList elementsRef={refs.listItemRefs} labelsRef={refs.labelsRef}>
            {/* TODO: keep grouping when filtering as well */}
            {field.value ? filtered : children}
          </FloatingList>
        </div>
      </div>
    );
  }
);

SingleSelectionMenu.displayName = 'SingleSelectionMenu';
